import React, { useState, useEffect } from 'react';
import { useMatchPoints, useMatchPointsOperations } from '../../../shared/hooks/useMatchPoints';
import { type MatchPointsData } from '../../../shared/services/matchPointsService';

interface MatchPointsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  match: {
    id: string;
    name: string;
    home_team: { id: string; name: string; logo?: string };
    away_team: { id: string; name: string; logo?: string };
    home_score: number;
    away_score: number;
    homePenaltyScore?: number;
    awayPenaltyScore?: number;
    tournament_id: string;
    season_id?: string;
  };
}

interface MatchPointsForm {
  homeResult: 'win' | 'draw' | 'loss';
  awayResult: 'win' | 'draw' | 'loss';
  homePoints: number;
  awayPoints: number;
  homeGoalsFor: number;
  homeGoalsAgainst: number;
  awayGoalsFor: number;
  awayGoalsAgainst: number;
}

const MatchPointsModal: React.FC<MatchPointsModalProps> = ({ 
  isOpen, 
  onClose, 
  onSuccess, 
  match 
}) => {
  console.log('MatchPointsModal - match received:', match);
  console.log('Penalty scores:', { 
    homePenaltyScore: match.homePenaltyScore, 
    awayPenaltyScore: match.awayPenaltyScore 
  });
  
  const [formData, setFormData] = useState<MatchPointsForm>({
    homeResult: 'draw',
    awayResult: 'draw',
    homePoints: 1,
    awayPoints: 1,
    homeGoalsFor: 0,
    homeGoalsAgainst: 0,
    awayGoalsFor: 0,
    awayGoalsAgainst: 0,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get existing match points if any
  const { matchPoints } = useMatchPoints(match.id);
  const { createMatchPoints, updateMatchPoints, loading: operationsLoading } = useMatchPointsOperations();

  // Initialize form with match data
  useEffect(() => {
    if (match) {
      setFormData({
        homeResult: 'draw',
        awayResult: 'draw',
        homePoints: 1,
        awayPoints: 1,
        homeGoalsFor: match.home_score,
        homeGoalsAgainst: match.away_score,
        awayGoalsFor: match.away_score,
        awayGoalsAgainst: match.home_score,
      });
    }
  }, [match]);

  // Load existing match points if available
  useEffect(() => {
    if (matchPoints && matchPoints.length > 0) {
      const homeTeamPoints = matchPoints.find(p => p.team_id === match.home_team.id);
      const awayTeamPoints = matchPoints.find(p => p.team_id === match.away_team.id);

      if (homeTeamPoints && awayTeamPoints) {
        setFormData({
          homeResult: homeTeamPoints.result,
          awayResult: awayTeamPoints.result,
          homePoints: homeTeamPoints.points_awarded,
          awayPoints: awayTeamPoints.points_awarded,
          homeGoalsFor: homeTeamPoints.goals_for,
          homeGoalsAgainst: homeTeamPoints.goals_against,
          awayGoalsFor: awayTeamPoints.goals_for,
          awayGoalsAgainst: awayTeamPoints.goals_against,
        });
      }
    }
  }, [matchPoints, match]);

  const handleInputChange = (field: keyof MatchPointsForm, value: string | number | undefined) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Auto-calculate result based on goals
    if (field === 'homeGoalsFor' || field === 'homeGoalsAgainst') {
      setFormData(currentData => {
        const homeGoals = field === 'homeGoalsFor' ? Number(value) : currentData.homeGoalsFor;
        const awayGoals = field === 'homeGoalsAgainst' ? Number(value) : currentData.homeGoalsAgainst;
        
        if (homeGoals > awayGoals) {
          return { 
            ...currentData, 
            homeResult: 'win', 
            awayResult: 'loss',
            homePoints: 3,
            awayPoints: 0
          };
        } else if (homeGoals < awayGoals) {
          return { 
            ...currentData, 
            homeResult: 'loss', 
            awayResult: 'win',
            homePoints: 0,
            awayPoints: 3
          };
        } else {
          return { 
            ...currentData, 
            homeResult: 'draw', 
            awayResult: 'draw',
            homePoints: 1,
            awayPoints: 1
          };
        }
      });
    }

    if (field === 'awayGoalsFor' || field === 'awayGoalsAgainst') {
      setFormData(currentData => {
        const homeGoals = field === 'awayGoalsAgainst' ? Number(value) : currentData.homeGoalsFor;
        const awayGoals = field === 'awayGoalsFor' ? Number(value) : currentData.awayGoalsFor;
        
        if (homeGoals > awayGoals) {
          return { 
            ...currentData, 
            homeResult: 'win', 
            awayResult: 'loss',
            homePoints: 3,
            awayPoints: 0
          };
        } else if (homeGoals < awayGoals) {
          return { 
            ...currentData, 
            homeResult: 'loss', 
            awayResult: 'win',
            homePoints: 0,
            awayPoints: 3
          };
        } else {
          return { 
            ...currentData, 
            homeResult: 'draw', 
            awayResult: 'draw',
            homePoints: 1,
            awayPoints: 1
          };
        }
      });
    }
  };

  const handleResultChange = (team: 'home' | 'away', result: 'win' | 'draw' | 'loss') => {
    if (team === 'home') {
      setFormData(prev => ({ 
        ...prev, 
        homeResult: result,
        awayResult: result === 'win' ? 'loss' : result === 'loss' ? 'win' : 'draw',
        homePoints: result === 'win' ? 3 : result === 'draw' ? 1 : 0,
        awayPoints: result === 'win' ? 0 : result === 'loss' ? 3 : 1
      }));
    } else {
      setFormData(prev => ({ 
        ...prev, 
        awayResult: result,
        homeResult: result === 'win' ? 'loss' : result === 'loss' ? 'win' : 'draw',
        awayPoints: result === 'win' ? 3 : result === 'draw' ? 1 : 0,
        homePoints: result === 'win' ? 0 : result === 'loss' ? 3 : 1
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const matchPointsData: MatchPointsData = {
        matchId: match.id,
        homeTeamId: match.home_team.id,
        awayTeamId: match.away_team.id,
        homeResult: formData.homeResult,
        awayResult: formData.awayResult,
        homePoints: formData.homePoints,
        awayPoints: formData.awayPoints,
        homeGoalsFor: formData.homeGoalsFor,
        homeGoalsAgainst: formData.homeGoalsAgainst,
        awayGoalsFor: formData.awayGoalsFor,
        awayGoalsAgainst: formData.awayGoalsAgainst,
      };

      if (matchPoints && matchPoints.length > 0) {
        // Update existing match points
        await updateMatchPoints(match.id, matchPointsData);
      } else {
        // Create new match points
        await createMatchPoints(matchPointsData);
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save match points');
      console.error('Match points error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto relative">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Match Points</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Match Teams Display */}
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center space-x-2">
                {match.home_team.logo && (
                  <img src={match.home_team.logo} alt={match.home_team.name} className="w-6 h-6 rounded-full" />
                )}
                <span className="font-medium text-gray-900">{match.home_team.name}</span>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-gray-900">
                  {match.home_score} - {match.away_score}
                </div>
                {/* Show penalty scores if they exist and are greater than 0 */}
                {match.homePenaltyScore !== undefined && match.awayPenaltyScore !== undefined && 
                 (match.homePenaltyScore > 0 || match.awayPenaltyScore > 0) && (
                  <div className="text-xs text-gray-600 mt-1">
                    penalties ({match.homePenaltyScore}) - ({match.awayPenaltyScore})
                  </div>
                )}
              </div>
              <div className="flex items-center space-x-2">
                <span className="font-medium text-gray-900">{match.away_team.name}</span>
                {match.away_team.logo && (
                  <img src={match.away_team.logo} alt={match.away_team.name} className="w-6 h-6 rounded-full" />
                )}
              </div>
            </div>
          </div>

          {/* Goals Section */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-2">Goals</h3>
            
            <div className="space-y-3">
              {/* Home Team Goals */}
              <div className="flex items-center space-x-3">
                <div className="flex-1">
                  <label className="block text-xs text-gray-600 mb-1">
                    {match.home_team.name} Goals For
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.homeGoalsFor}
                    onChange={(e) => handleInputChange('homeGoalsFor', parseInt(e.target.value) || 0)}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-xs text-gray-600 mb-1">
                    {match.home_team.name} Goals Against
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.homeGoalsAgainst}
                    onChange={(e) => handleInputChange('homeGoalsAgainst', parseInt(e.target.value) || 0)}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              {/* Away Team Goals */}
              <div className="flex items-center space-x-3">
                <div className="flex-1">
                  <label className="block text-xs text-gray-600 mb-1">
                    {match.away_team.name} Goals For
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.awayGoalsFor}
                    onChange={(e) => handleInputChange('awayGoalsFor', parseInt(e.target.value) || 0)}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-xs text-gray-600 mb-1">
                    {match.away_team.name} Goals Against
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.awayGoalsAgainst}
                    onChange={(e) => handleInputChange('awayGoalsAgainst', parseInt(e.target.value) || 0)}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Result & Points Section */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-2">Match Result & Points</h3>
            
            <div className="space-y-3">
              {/* Home Team Result & Points */}
              <div className="flex items-center space-x-3">
                <div className="flex-1">
                  <label className="block text-xs text-gray-600 mb-1">
                    {match.home_team.name} Result
                  </label>
                  <select
                    value={formData.homeResult}
                    onChange={(e) => handleResultChange('home', e.target.value as 'win' | 'draw' | 'loss')}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="win">Win</option>
                    <option value="draw">Draw</option>
                    <option value="loss">Loss</option>
                  </select>
                </div>
                <div className="flex-1">
                  <label className="block text-xs text-gray-600 mb-1">
                    {match.home_team.name} Points
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.homePoints}
                    onChange={(e) => handleInputChange('homePoints', parseInt(e.target.value) || 0)}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              {/* Away Team Result & Points */}
              <div className="flex items-center space-x-3">
                <div className="flex-1">
                  <label className="block text-xs text-gray-600 mb-1">
                    {match.away_team.name} Result
                  </label>
                  <select
                    value={formData.awayResult}
                    onChange={(e) => handleResultChange('away', e.target.value as 'win' | 'draw' | 'loss')}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="win">Win</option>
                    <option value="draw">Draw</option>
                    <option value="loss">Loss</option>
                  </select>
                </div>
                <div className="flex-1">
                  <label className="block text-xs text-gray-600 mb-1">
                    {match.away_team.name} Points
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.awayPoints}
                    onChange={(e) => handleInputChange('awayPoints', parseInt(e.target.value) || 0)}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>
          </div>


          {/* Error Message */}
          {error && (
            <div className="text-red-600 text-sm bg-red-50 border border-red-200 rounded px-3 py-2">
              {error}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || operationsLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading || operationsLoading ? 'Saving...' : 'Save Match Points'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MatchPointsModal;
